import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import EditableImage from "../../landingEditor/EditableImage";
import { getValueAtDescriptorPath } from "../elementEditing";
import { registryFieldError, persistedCssColorError } from "../inspector/inspectorModel";
import { CATALOG_CARD_COLOR_FALLBACKS, resolveCatalogCardButtonColors } from "../catalogCardColors";
import LandingCopyTextField from "../inspector/LandingCopyTextField";
import { VisualColorControl, VisualSliderControl } from "../inspector/VisualSettingControls";

const GROUPS = Object.freeze({
  distribution: ["gap", "radius", "imagePosition", "imageFit", "imageHeight"],
  visibility: [
    "showImage", "showTitle", "showType", "showCategory", "showSubcategory",
    "showDescription", "showPrice", "showDuration", "showCapabilities", "showQuickCart", "showButton",
  ],
  background: ["backgroundColor", "backgroundOpacity", "backgroundImageUrl", "backgroundImagePosition", "backgroundImageFit", "backgroundImageOpacity"],
  copy: [
    "typeColor", "titleColor", "categoryColor", "subcategoryColor", "descriptionColor", "priceColor",
    "buttonTextColor", "buttonBackgroundColor", "buttonHoverTextColor", "buttonHoverBackgroundColor",
    "ctaLabel", "serviceCtaLabel", "ctaBehavior", "ctaUrl",
  ],
  borderShadow: ["borderColor", "shadowColor", "shadowOpacity", "shadowX", "shadowY", "shadowBlur", "shadowSpread"],
});


const CARD_NUMBER_DEFAULTS = Object.freeze({
  gap: 18,
  radius: 16,
  backgroundOpacity: 1,
  backgroundImageOpacity: 0.28,
  imageHeight: 210,
  shadowOpacity: 1,
  shadowX: 0,
  shadowY: 8,
  shadowBlur: 24,
  shadowSpread: 0,
});
const DISPLAY_LABELS = Object.freeze({
  showImage: "Imagen",
  showTitle: "Título",
  showType: "Tipo",
  showCategory: "Categoría",
  showSubcategory: "Subcategoría",
  showDescription: "Descripción",
  showPrice: "Precio",
  showDuration: "Duración",
  showCapabilities: "Reserva / entrega",
  showQuickCart: "Compra rápida",
  showButton: "Botón",
});

function alphaColor(value, opacity, fallback) {
  const color = String(value || fallback || "transparent").trim() || "transparent";
  const alpha = Math.max(0, Math.min(1, Number.isFinite(Number(opacity)) ? Number(opacity) : 1));
  if (alpha <= 0) return "transparent";
  if (alpha >= 0.999) return color;
  return `color-mix(in srgb, ${color} ${Math.round(alpha * 1000) / 10}%, transparent)`;
}

function fieldMap(descriptor) {
  return new Map((descriptor?.fields || []).map((field) => [String(field.path || "").split(".").pop(), field]));
}

function valueFor(form, field, fallback = "") {
  return field ? getValueAtDescriptorPath(form, field.path, field.defaultValue ?? fallback) : fallback;
}

function NumberControl({ field, form, disabled, onCommit }) {
  if (!field) return null;
  const suffix = String(field.path || "").split(".").pop();
  const fallback = field.defaultValue ?? CARD_NUMBER_DEFAULTS[suffix] ?? field.limits?.min ?? 0;
  const value = valueFor(form, field, fallback);
  return (
    <VisualSliderControl
      label={field.label}
      value={value}
      min={field.limits?.min ?? 0}
      max={field.limits?.max ?? 100}
      step={field.limits?.step ?? 1}
      disabled={disabled}
      onChange={(next) => onCommit(field.path, next)}
    />
  );
}


function SelectControl({ field, form, disabled, onCommit }) {
  if (!field) return null;
  return (
    <FormControl fullWidth size="small" disabled={disabled}>
      <InputLabel>{field.label}</InputLabel>
      <Select label={field.label} value={valueFor(form, field, "") ?? ""} onChange={(event) => onCommit(field.path, event.target.value)}>
        {(field.options || []).map((option) => <MenuItem key={`${field.path}:${option.value}`} value={option.value}>{option.label}</MenuItem>)}
      </Select>
    </FormControl>
  );
}

function TextControl({ field, form, baselineForm, disabled, onCommit, helperText = "" }) {
  if (!field) return null;
  return (
    <LandingCopyTextField
      field={field}
      form={form}
      baselineForm={baselineForm}
      fullWidth
      size="small"
      label={field.label}
      value={valueFor(form, field, "") ?? ""}
      disabled={disabled}
      helperText={helperText}
      commitMode="change"
      onCommit={(value) => onCommit(field.path, value)}
    />
  );
}

function ColorControl({ field, form, disabled, onCommit, fallback = "#000000" }) {
  if (!field) return null;
  const own = String(valueFor(form, field, "") || "").trim();
  return (
    <VisualColorControl
      label={field.label}
      value={own}
      fallback={fallback}
      disabled={disabled}
      helperText={own ? "Color propio de las cards (Destacados y Catálogo)." : "Heredado: usa el valor predeterminado de la web pública."}
      allowExactCss
      validateExactValue={(candidate) => registryFieldError(field, candidate) || persistedCssColorError(candidate, field.maxLength || 100)}
      exactValueMaxLength={field.maxLength || 100}
      exactValueHelperText="HEX, rgb/rgba, hsl/hsla o transparent. Vacío vuelve a heredar."
      onChange={(next) => onCommit(field.path, next)}
      onReset={() => onCommit(field.path, "")}
    />
  );
}


function ShadowPreview({ values, hover }) {
  const shadowOpacity = values.shadowOpacity === "" || values.shadowOpacity === undefined ? 1 : Number(values.shadowOpacity);
  const shadowColor = alphaColor(values.shadowColor || "#000000", shadowOpacity, "#000000");
  const shadow = shadowOpacity <= 0
    ? "none"
    : `${Number(values.shadowX ?? 0) || 0}px ${Number(values.shadowY ?? 8) || 0}px ${Number(values.shadowBlur ?? 24) || 0}px ${Number(values.shadowSpread ?? 0) || 0}px ${shadowColor}`;
  const background = alphaColor(values.backgroundColor || "#ffffff", values.backgroundOpacity ?? 1, "#ffffff");
  const imagePosition = values.imagePosition || "top";
  const horizontal = imagePosition === "left" || imagePosition === "right";
  // La vista de hover comparte la misma precedencia que la tienda publicada.
  const buttonColors = resolveCatalogCardButtonColors(values);
  const buttonColor = hover ? buttonColors.hoverText : buttonColors.text;
  const buttonBackground = hover ? buttonColors.hoverBackground : buttonColors.background;
  const imageBg = values.backgroundImageUrl ? `url("${String(values.backgroundImageUrl).replace(/"/g, "%22")}")` : "none";

  return (
    <Box sx={{ p: 2.2, borderRadius: 2.5, bgcolor: "rgba(25,36,47,.035)", overflow: "hidden" }}>
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: horizontal ? (imagePosition === "right" ? "row-reverse" : "row") : "column",
          maxWidth: 300,
          mx: "auto",
          borderRadius: `${Number(values.radius ?? 16) || 0}px`,
          border: `1px solid ${values.borderColor || "rgba(25,36,47,.14)"}`,
          bgcolor: background,
          boxShadow: shadow,
          transform: hover ? "translateY(-2px)" : "none",
          transition: "all .16s ease",
          isolation: "isolate",
          "&::before": values.backgroundImageUrl ? {
            content: '\"\"',
            position: "absolute",
            inset: 0,
            zIndex: 0,
            backgroundImage: imageBg,
            backgroundSize: values.backgroundImageFit === "contain" ? "contain" : "cover",
            backgroundPosition: values.backgroundImagePosition || "center",
            backgroundRepeat: "no-repeat",
            opacity: Number(values.backgroundImageOpacity ?? .28),
          } : undefined,
          "& > *": { position: "relative", zIndex: 1 },
        }}
      >
        {values.showImage !== false ? (
          <Box sx={{ width: horizontal ? 82 : "100%", minWidth: horizontal ? 82 : 0, height: horizontal ? "auto" : Math.min(150, Number(values.imageHeight ?? 210) || 210), bgcolor: "rgba(106,90,224,.14)", background: "linear-gradient(135deg, rgba(106,90,224,.18), rgba(25,36,47,.08))" }} />
        ) : null}
        <Stack spacing={0.45} sx={{ p: 1.4, flex: 1, minWidth: 0 }}>
          {values.showType !== false ? <Typography variant="caption" sx={{ color: values.typeColor || CATALOG_CARD_COLOR_FALLBACKS.type, fontWeight: 850 }}>Producto</Typography> : null}
          {values.showTitle !== false ? <Typography sx={{ color: values.titleColor || CATALOG_CARD_COLOR_FALLBACKS.title, fontWeight: 900, fontSize: ".92rem" }}>Producto de ejemplo</Typography> : null}
          {values.showCategory !== false ? <Typography variant="caption" sx={{ color: values.categoryColor || CATALOG_CARD_COLOR_FALLBACKS.category }}>Categoría</Typography> : null}
          {values.showSubcategory !== false ? <Typography variant="caption" sx={{ color: values.subcategoryColor || CATALOG_CARD_COLOR_FALLBACKS.subcategory }}>Subcategoría</Typography> : null}
          {values.showDescription !== false ? <Typography variant="caption" sx={{ color: values.descriptionColor || CATALOG_CARD_COLOR_FALLBACKS.description }}>Descripción breve de ejemplo.</Typography> : null}
          {values.showDuration !== false ? <Typography variant="caption" sx={{ color: values.descriptionColor || CATALOG_CARD_COLOR_FALLBACKS.description }}>Duración: 60 min</Typography> : null}
          {values.showCapabilities !== false ? <Stack direction="row" spacing={.45} useFlexGap flexWrap="wrap"><Chip size="small" label="Reserva" /><Chip size="small" label="Entrega" /></Stack> : null}
          {values.showPrice !== false ? <Typography sx={{ color: values.priceColor || CATALOG_CARD_COLOR_FALLBACKS.price, fontWeight: 950 }}>$ 12.500</Typography> : null}
          {values.showQuickCart !== false ? <Chip size="small" icon={<ShoppingCartOutlinedIcon />} label="Compra rápida" sx={{ alignSelf: "flex-start" }} /> : null}
          {values.showButton !== false ? (
            <Button size="small" variant="contained" sx={{ mt: .4, alignSelf: "flex-start", textTransform: "none", color: buttonColor || undefined, bgcolor: buttonBackground || undefined, "&:hover": { bgcolor: buttonBackground || undefined } }}>
              {values.ctaLabel || "Ver detalle"}
            </Button>
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
}

function EditorGroup({ title, subtitle, defaultExpanded = false, children }) {
  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters elevation={0} sx={{ border: "1px solid rgba(25,36,47,.10)", borderRadius: "12px !important", "&::before": { display: "none" }, overflow: "hidden" }}>
      <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ minHeight: 52, "& .MuiAccordionSummary-content": { my: 1 } }}>
        <Stack spacing={0.15}>
          <Typography variant="body2" sx={{ fontWeight: 950 }}>{title}</Typography>
          {subtitle ? <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>{subtitle}</Typography> : null}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}><Stack spacing={1.2}>{children}</Stack></AccordionDetails>
    </Accordion>
  );
}

export default function CardSystemEditor({ descriptor, form, baselineForm, disabled, onCommit, onUploadImage, onRemoveImage }) {
  const fields = React.useMemo(() => fieldMap(descriptor), [descriptor]);
  const [previewState, setPreviewState] = React.useState("normal");
  const field = (suffix) => fields.get(suffix) || null;
  const read = (suffix, fallback) => valueFor(form, field(suffix), fallback);
  const values = React.useMemo(() => {
    const output = {};
    [...GROUPS.distribution, ...GROUPS.visibility, ...GROUPS.background, ...GROUPS.copy, ...GROUPS.borderShadow].forEach((suffix) => {
      output[suffix] = read(suffix, undefined);
    });
    return output;
  }, [form, fields]);

  const backgroundImageField = field("backgroundImageUrl");
  const behavior = read("ctaBehavior", "detail");

  return (
    <Stack spacing={1.3}>
      <Alert severity="info" icon={false} sx={{ py: .6 }}>
        Estos colores se comparten entre Productos destacados (inicio) y Catálogo. Si dejás un color vacío, se usa el mismo valor predeterminado en ambas vistas.
      </Alert>

      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="caption" sx={{ fontWeight: 900, color: "rgba(25,36,47,.62)" }}>Preview</Typography>
        <ToggleButtonGroup exclusive size="small" value={previewState} onChange={(_, value) => value && setPreviewState(value)} aria-label="Estado del preview de la card">
          <ToggleButton value="normal" aria-label="Vista normal">Normal</ToggleButton>
          <ToggleButton value="hover" aria-label="Vista hover">Hover</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <ShadowPreview values={values} hover={previewState === "hover"} />

      <EditorGroup title="Distribución" subtitle="Forma, separación e imagen" defaultExpanded>
        <NumberControl field={field("gap")} form={form} disabled={disabled} onCommit={onCommit} />
        <NumberControl field={field("radius")} form={form} disabled={disabled} onCommit={onCommit} />
        <SelectControl field={field("imagePosition")} form={form} disabled={disabled} onCommit={onCommit} />
        <SelectControl field={field("imageFit")} form={form} disabled={disabled} onCommit={onCommit} />
        <NumberControl field={field("imageHeight")} form={form} disabled={disabled} onCommit={onCommit} />
      </EditorGroup>

      <EditorGroup title="Qué mostrar" subtitle="Activá sólo la información útil">
        <Stack direction="row" spacing={.65} useFlexGap flexWrap="wrap">
          {GROUPS.visibility.map((suffix) => {
            const currentField = field(suffix);
            if (!currentField) return null;
            const checked = read(suffix, true) !== false;
            return (
              <Chip
                key={suffix}
                size="small"
                clickable={!disabled}
                label={DISPLAY_LABELS[suffix] || currentField.label}
                variant={checked ? "filled" : "outlined"}
                onClick={disabled ? undefined : () => onCommit(currentField.path, !checked)}
                aria-pressed={checked}
              />
            );
          })}
        </Stack>
        <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>
          Activo = visible. Tocá cada chip para mostrar u ocultar ese dato.
        </Typography>
      </EditorGroup>

      <EditorGroup title="Fondo e imagen" subtitle="Color, transparencia e imagen de fondo">
        <ColorControl field={field("backgroundColor")} form={form} disabled={disabled} onCommit={onCommit} fallback="#ffffff" />
        <NumberControl field={field("backgroundOpacity")} form={form} disabled={disabled} onCommit={onCommit} />
        {backgroundImageField ? (
          <EditableImage
            value={read("backgroundImageUrl", "")}
            label={backgroundImageField.label || "Imagen de fondo"}
            disabled={disabled}
            height={130}
            onUpload={(file, metadata) => onUploadImage?.(backgroundImageField.path, file, metadata)}
            onRemove={() => onRemoveImage?.(backgroundImageField.path)}
          />
        ) : null}
        <SelectControl field={field("backgroundImagePosition")} form={form} disabled={disabled} onCommit={onCommit} />
        <SelectControl field={field("backgroundImageFit")} form={form} disabled={disabled} onCommit={onCommit} />
        <NumberControl field={field("backgroundImageOpacity")} form={form} disabled={disabled} onCommit={onCommit} />
      </EditorGroup>

      <EditorGroup title="Textos y botón" subtitle="Colores, copies y destino de acción">
        <ColorControl field={field("typeColor")} form={form} disabled={disabled} onCommit={onCommit} fallback={CATALOG_CARD_COLOR_FALLBACKS.type} />
        <ColorControl field={field("titleColor")} form={form} disabled={disabled} onCommit={onCommit} fallback={CATALOG_CARD_COLOR_FALLBACKS.title} />
        <ColorControl field={field("categoryColor")} form={form} disabled={disabled} onCommit={onCommit} fallback={CATALOG_CARD_COLOR_FALLBACKS.category} />
        <ColorControl field={field("subcategoryColor")} form={form} disabled={disabled} onCommit={onCommit} fallback={CATALOG_CARD_COLOR_FALLBACKS.subcategory} />
        <ColorControl field={field("descriptionColor")} form={form} disabled={disabled} onCommit={onCommit} fallback={CATALOG_CARD_COLOR_FALLBACKS.description} />
        <ColorControl field={field("priceColor")} form={form} disabled={disabled} onCommit={onCommit} fallback={CATALOG_CARD_COLOR_FALLBACKS.price} />
        <ColorControl field={field("buttonTextColor")} form={form} disabled={disabled} onCommit={onCommit} fallback={CATALOG_CARD_COLOR_FALLBACKS.buttonText} />
        <ColorControl field={field("buttonBackgroundColor")} form={form} disabled={disabled} onCommit={onCommit} fallback={CATALOG_CARD_COLOR_FALLBACKS.buttonBackground} />
        <ColorControl field={field("buttonHoverTextColor")} form={form} disabled={disabled} onCommit={onCommit} fallback={CATALOG_CARD_COLOR_FALLBACKS.buttonText} />
        <ColorControl field={field("buttonHoverBackgroundColor")} form={form} disabled={disabled} onCommit={onCommit} fallback={CATALOG_CARD_COLOR_FALLBACKS.buttonBackground} />
        <TextControl field={field("ctaLabel")} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} />
        <TextControl field={field("serviceCtaLabel")} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} />
        <SelectControl field={field("ctaBehavior")} form={form} disabled={disabled} onCommit={onCommit} />
        {behavior === "custom" ? <TextControl field={field("ctaUrl")} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} helperText="Podés usar {id} y {type} en el link." /> : null}
      </EditorGroup>

      <EditorGroup title="Borde y sombra" subtitle="Opciones avanzadas">
        <ColorControl field={field("borderColor")} form={form} disabled={disabled} onCommit={onCommit} fallback="#E1E4EA" />
        <ColorControl field={field("shadowColor")} form={form} disabled={disabled} onCommit={onCommit} fallback="#000000" />
        <NumberControl field={field("shadowOpacity")} form={form} disabled={disabled} onCommit={onCommit} />
        <NumberControl field={field("shadowX")} form={form} disabled={disabled} onCommit={onCommit} />
        <NumberControl field={field("shadowY")} form={form} disabled={disabled} onCommit={onCommit} />
        <NumberControl field={field("shadowBlur")} form={form} disabled={disabled} onCommit={onCommit} />
        <NumberControl field={field("shadowSpread")} form={form} disabled={disabled} onCommit={onCommit} />
      </EditorGroup>
    </Stack>
  );
}
